import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

const STATUS_CONFIG = {
  in_progress: { label: 'In Progress', color: 'primary', icon: '🚀' },
  planned: { label: 'Planned', color: 'info', icon: '📋' },
  shipped: { label: 'Shipped', color: 'success', icon: '✅' }
};

function Roadmap() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [features, setFeatures] = useState([]);
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Suggest modal state
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [suggestTitle, setSuggestTitle] = useState('');
  const [suggestDescription, setSuggestDescription] = useState('');
  const [suggestAttribution, setSuggestAttribution] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState('');

  useEffect(() => {
    checkAuthAndLoadData();
  }, []);

  const checkAuthAndLoadData = async () => {
    // Check if user is logged in (optional - page works without login)
    try {
      const authResult = await authService.checkAuth();
      if (authResult.authenticated) {
        setUser(authResult.user);
      }
    } catch (err) {
      // Not logged in - that's fine for this public page
    }

    // Load roadmap data
    try {
      const [featuresRes, releasesRes] = await Promise.all([
        fetch('/api/roadmap/features'),
        fetch('/api/roadmap/releases')
      ]);

      const featuresData = await featuresRes.json();
      const releasesData = await releasesRes.json();

      if (featuresData.success) {
        setFeatures(featuresData.features);
      }
      if (releasesData.success) {
        setReleases(releasesData.releases);
      }
    } catch (err) {
      setError('Failed to load roadmap');
    }
    setLoading(false);
  };

  const handleVote = async (featureId) => {
    if (!user) {
      setError('Please log in to vote');
      return;
    }

    try {
      const response = await fetch('/api/roadmap/vote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feature_id: featureId })
      });

      const data = await response.json();
      if (data.success) {
        // Update local state
        setFeatures(prev => prev.map(f =>
          f.id === featureId
            ? { ...f, votes_count: data.votes_count, user_voted: data.voted }
            : f
        ));
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to vote');
    }
  };

  const handleSuggest = async (e) => {
    e.preventDefault();
    if (!user) {
      setError('Please log in to suggest features');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/roadmap/suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: suggestTitle,
          description: suggestDescription,
          show_attribution: suggestAttribution
        })
      });

      const data = await response.json();
      if (data.success) {
        setSubmitSuccess(data.message);
        setSuggestTitle('');
        setSuggestDescription('');
        setSuggestAttribution(false);
        setTimeout(() => {
          setShowSuggestModal(false);
          setSubmitSuccess('');
        }, 3000);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to submit suggestion');
    }

    setSubmitting(false);
  };

  // Group features by status
  const inProgress = features.filter(f => f.status === 'in_progress');
  const planned = features.filter(f => f.status === 'planned');
  const shipped = features.filter(f => f.status === 'shipped');

  // Group planned by release
  const plannedByRelease = {};
  const plannedNoRelease = [];
  planned.forEach(f => {
    if (f.release_id) {
      if (!plannedByRelease[f.release_id]) {
        plannedByRelease[f.release_id] = {
          release: releases.find(r => r.id === f.release_id),
          features: []
        };
      }
      plannedByRelease[f.release_id].features.push(f);
    } else {
      plannedNoRelease.push(f);
    }
  });

  const FeatureCard = ({ feature }) => (
    <Card className="mb-3 shadow-sm">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 mb-2">
              <Button
                variant={feature.user_voted ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => handleVote(feature.id)}
                disabled={!user || feature.status === 'shipped'}
                title={!user ? 'Log in to vote' : feature.user_voted ? 'Click to remove vote' : 'Click to vote'}
                style={{ minWidth: '60px' }}
              >
                ▲ {feature.votes_count}
              </Button>
              {feature.category && (
                <Badge bg="secondary">{feature.category}</Badge>
              )}
            </div>
            <h6 className="mb-1">{feature.title}</h6>
            <p className="text-muted small mb-2">{feature.description}</p>
            <div className="d-flex gap-2 flex-wrap">
              {feature.submitted_by_name && (
                <small className="text-muted">Suggested by {feature.submitted_by_name}</small>
              )}
              {feature.release_name && (
                <Badge bg="light" text="dark">{feature.release_name}</Badge>
              )}
              {feature.ship_date && (
                <small className="text-success">Shipped {feature.ship_date}</small>
              )}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Header */}
      <div className="text-center mb-5">
        <h1 className="mb-3">Product Roadmap</h1>
        <p className="text-muted mb-4">
          See what we're building and suggest features you'd like to see.
        </p>
        {user ? (
          <Button variant="primary" onClick={() => setShowSuggestModal(true)}>
            Suggest a Feature
          </Button>
        ) : (
          <Button variant="outline-primary" onClick={() => navigate('/login')}>
            Log In to Suggest Features
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
          {error}
        </Alert>
      )}

      <Row>
        {/* In Progress */}
        <Col lg={4} className="mb-4">
          <h4 className="mb-3">
            <span className="me-2">{STATUS_CONFIG.in_progress.icon}</span>
            In Progress
            <Badge bg="primary" className="ms-2">{inProgress.length}</Badge>
          </h4>
          {inProgress.length === 0 ? (
            <p className="text-muted">Nothing in progress right now</p>
          ) : (
            inProgress.map(f => <FeatureCard key={f.id} feature={f} />)
          )}
        </Col>

        {/* Planned */}
        <Col lg={4} className="mb-4">
          <h4 className="mb-3">
            <span className="me-2">{STATUS_CONFIG.planned.icon}</span>
            Planned
            <Badge bg="info" className="ms-2">{planned.length}</Badge>
          </h4>
          {planned.length === 0 ? (
            <p className="text-muted">No features planned yet</p>
          ) : (
            <>
              {/* Grouped by release */}
              {Object.values(plannedByRelease).map(group => (
                <div key={group.release?.id} className="mb-4">
                  <h6 className="text-muted border-bottom pb-2">
                    {group.release?.name || 'Upcoming'}
                    {group.release?.target_date && (
                      <small className="ms-2">({group.release.target_date})</small>
                    )}
                  </h6>
                  {group.features.map(f => <FeatureCard key={f.id} feature={f} />)}
                </div>
              ))}
              {/* No release */}
              {plannedNoRelease.length > 0 && (
                <div>
                  <h6 className="text-muted border-bottom pb-2">Upcoming</h6>
                  {plannedNoRelease.map(f => <FeatureCard key={f.id} feature={f} />)}
                </div>
              )}
            </>
          )}
        </Col>

        {/* Recently Shipped */}
        <Col lg={4} className="mb-4">
          <h4 className="mb-3">
            <span className="me-2">{STATUS_CONFIG.shipped.icon}</span>
            Recently Shipped
            <Badge bg="success" className="ms-2">{shipped.length}</Badge>
          </h4>
          {shipped.length === 0 ? (
            <p className="text-muted">Check back soon!</p>
          ) : (
            shipped.map(f => <FeatureCard key={f.id} feature={f} />)
          )}
        </Col>
      </Row>

      {/* Suggest Modal */}
      <Modal show={showSuggestModal} onHide={() => setShowSuggestModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Suggest a Feature</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSuggest}>
          <Modal.Body>
            {submitSuccess ? (
              <Alert variant="success">{submitSuccess}</Alert>
            ) : (
              <>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Brief title for your feature"
                    value={suggestTitle}
                    onChange={(e) => setSuggestTitle(e.target.value)}
                    maxLength={200}
                    required
                  />
                  <Form.Text className="text-muted">
                    {suggestTitle.length}/200 characters
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="What problem does this solve? What would you like to see?"
                    value={suggestDescription}
                    onChange={(e) => setSuggestDescription(e.target.value)}
                    required
                  />
                  <Form.Text className="text-muted">
                    Minimum 20 characters
                  </Form.Text>
                </Form.Group>
                <Form.Check
                  type="checkbox"
                  label="Show my name publicly as the suggester"
                  checked={suggestAttribution}
                  onChange={(e) => setSuggestAttribution(e.target.checked)}
                />
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSuggestModal(false)}>
              Cancel
            </Button>
            {!submitSuccess && (
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Suggestion'}
              </Button>
            )}
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default Roadmap;
